import { FieldValues } from 'react-hook-form';
import { BusinessCase } from '@schuettflix/interfaces';
import { FinanceSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import { CustomerInfo } from '@/shared/models/ContextDataModel/interfaces/entities/customer-info.ts';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration.ts';

export type UpsertFinanceSettings = Partial<FinanceSettings> & {
    creditorOrganizationId: number;
    debtorOrganizationId: number;
};

export interface ContextDataInterface<TData extends FieldValues> {
    setFormData: (formData: Partial<TData>) => void;
    getFormData: () => Partial<TData>;
    getBusinessCase: () => BusinessCase | undefined;
    setBusinessCase: (businessCase: BusinessCase | undefined, defaultValues?: Partial<TData>) => void;
    getFinanceSettings: () => FinanceSettings[];
    setFinanceSettings: (financeSettings: FinanceSettings[]) => void;
    upsertFinanceSetting: (financeSettings: UpsertFinanceSettings) => void;
    getCustomerInformation: () => CustomerInfo | undefined;
    setCustomerInformation: (customerInformation: CustomerInfo) => void;
    setProductConfigurations(productConfigurations: ProductConfiguration[]): void;
    getProductConfigurations(): ProductConfiguration[] | undefined;
}

export interface ContextDataConstructorParams<TData extends FieldValues> {
    customerInformation?: CustomerInfo;
    businessCase?: BusinessCase;
    defaultValues?: Partial<TData>;
    financeSettings?: FinanceSettings[];
    productConfigurations?: ProductConfiguration[];
}

export class ContextData<TData extends FieldValues> implements ContextDataInterface<TData> {
    protected formData: Partial<TData> = {};
    protected businessCase: BusinessCase | undefined;
    protected financeSettings: FinanceSettings[] = [];
    protected customerInformation: CustomerInfo | undefined;
    protected productConfigurations: ProductConfiguration[] | undefined = undefined;

    constructor({
        businessCase,
        customerInformation,
        financeSettings,
        productConfigurations,
        defaultValues,
    }: ContextDataConstructorParams<TData>) {
        if (defaultValues) {
            this.formData = { ...defaultValues };
        }

        if (customerInformation) {
            this.customerInformation = customerInformation;
        }

        if (businessCase) {
            this.businessCase = businessCase;
        }

        if (productConfigurations) {
            this.productConfigurations = productConfigurations;
        }

        if (financeSettings) {
            this.financeSettings = financeSettings;
        }
    }

    setFormData(formData: Partial<TData>) {
        this.formData = { ...this.formData, ...formData };
    }

    getFormData() {
        return this.formData;
    }

    getBusinessCase() {
        return this.businessCase;
    }

    setBusinessCase(businessCase: BusinessCase | undefined, defaultValues?: Partial<TData>) {
        this.businessCase = businessCase;

        if (businessCase === undefined) {
            this.formData = {};
            this.financeSettings = [];
            this.productConfigurations = undefined;
        }

        if (defaultValues) {
            this.formData = { ...this.formData, ...defaultValues };
        }
    }

    getFinanceSettings() {
        return Array.from(this.financeSettings.values());
    }

    setFinanceSettings(financeSettings: FinanceSettings[]) {
        this.financeSettings = financeSettings;
    }

    upsertFinanceSetting(financeSettings: UpsertFinanceSettings) {
        const entryIndex = this.financeSettings.findIndex(({ creditorOrganizationId, debtorOrganizationId }) => {
            return (
                creditorOrganizationId === financeSettings.creditorOrganizationId &&
                debtorOrganizationId === financeSettings.debtorOrganizationId
            );
        });

        if (entryIndex > -1) {
            const newFinanceSettings = [...this.financeSettings];

            newFinanceSettings[entryIndex] = {
                ...newFinanceSettings[entryIndex],
                ...financeSettings,
            } as FinanceSettings;

            this.financeSettings = newFinanceSettings;
        } else {
            this.financeSettings = [...this.financeSettings, financeSettings as FinanceSettings];
        }
    }

    getCustomerInformation() {
        return this.customerInformation;
    }

    setCustomerInformation(customerInformation: CustomerInfo) {
        this.customerInformation = customerInformation;
    }

    getProductConfigurations() {
        return this.productConfigurations;
    }

    setProductConfigurations(productConfigurations: ProductConfiguration[]) {
        this.productConfigurations = productConfigurations;
    }
}
